import { useEffect, useRef, useState } from "react";
import api from "../api/api";
import '../styles/AboutUs.css';
import Header from "./Components/Header";
import {IMAGE_URL} from "../path";
import Footer from "./Components/Footer";
import {Link} from "react-router-dom";
import { decode } from "html-entities";
import cartonBackground from "../assets/images/carton-background.jpg";

type AboutUsData = {
    id: number;
    header_image: string;
    body: string;
    image_1: string;
    body_1: string;
    body_2: string;
    image_2: string;
};

type AboutUsResponse = {
    message: string;
    about: AboutUsData | null;
};

function AboutUs() {
    const [about, setAbout] = useState<AboutUsData | null>(null);
    const [loading, setLoading] = useState(true);

    const fetchAboutUs = async () => {
        try {
            const response = await api.get<AboutUsResponse>("/fetch-about-us");

            setAbout(response.data.about);
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const image2Ref = useRef<HTMLDivElement>(null);

    const [image1Visible, setImage1Visible] = useState(false);
    const [image2Visible, setImage2Visible] = useState(false);

    useEffect(() => {
        fetchAboutUs();
    }, []);

    useEffect(() => {
        if (loading) return;

        const timer = setTimeout(() => {
            setImage1Visible(true);
        }, 150);

        return () => clearTimeout(timer);
    }, [loading]);

    useEffect(() => {
        if (loading || !image2Ref.current) return;

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setImage2Visible(true);
                    observer.disconnect();
                }
            },
            {
                threshold: 0.25,
            }
        );

        observer.observe(image2Ref.current);

        return () => observer.disconnect();
    }, [loading]);

    if (loading) {
        return (
            <div>
                <div
                    className="about-us-page-header"
                    style={{
                        backgroundImage: `url(${cartonBackground})`,
                    }}
                >
                    <Header />

                    <div className="about-us-header-inner">
                        <div className="skeleton about-title"></div>
                        <div className="skeleton about-text"></div>
                        <div className="skeleton about-text"></div>
                        <div className="skeleton about-text short"></div>
                    </div>
                </div>

                <div className="about-us-page-content">
                    <div className="about-us-content-inner">
                        <div className="about-us-inner-image">
                            <div className="skeleton about-image"></div>
                        </div>

                        <div className="about-us-inner-body">
                            <div className="skeleton about-body-line"></div>
                            <div className="skeleton about-body-line"></div>
                            <div className="skeleton about-body-line"></div>
                            <div className="skeleton about-body-line short"></div>
                        </div>
                    </div>

                    <div className="about-us-content-inner">
                        <div className="about-us-inner-body">
                            <div className="skeleton about-body-line"></div>
                            <div className="skeleton about-body-line"></div>
                            <div className="skeleton about-body-line"></div>
                            <div className="skeleton about-body-line short"></div>

                            <div
                                className="skeleton"
                                style={{
                                    width: "180px",
                                    height: "50px",
                                    marginTop: "30px",
                                }}
                            />
                        </div>

                        <div className="about-us-inner-image">
                            <div className="skeleton about-image"></div>
                        </div>
                    </div>
                </div>

                <Footer />
            </div>
        );
    }

    return (
        <div>
            <div className="about-us-page-header" style={{
                backgroundImage: about?.header_image
                    ? `url(${IMAGE_URL}/${about.header_image})`
                    : `url(${cartonBackground})`,
            }}>
                <Header />
                <div className="about-us-header-inner">
                    <h1>About Us</h1>
                    <div dangerouslySetInnerHTML={{__html: decode(decode(about?.body ?? ''))}} />
                </div>
            </div>
            <div className="about-us-page-content">
                <div className="about-us-content-inner">
                    <div
                        className="about-us-inner-image slide-left-on-load"
                    >
                        <img src={`${IMAGE_URL}/${about?.image_1}`} alt={about?.image_1} />
                    </div>
                    <div className="about-us-inner-body">
                        <div
                            dangerouslySetInnerHTML={{
                                __html: decode(decode(about?.body_1 ?? ""))
                            }}
                        />
                    </div>


                </div>
                <div className="about-us-content-inner">
                    <div className="about-us-inner-body">
                        <div dangerouslySetInnerHTML={{__html: decode(decode(about?.body_2 ?? ''))}} />
                        <div className="about-us-inner-button">
                            <Link to="/recipes" className="about-recipe-link">
                                Recipe
                            </Link>
                        </div>
                    </div>
                    <div
                        ref={image2Ref}
                        className={`about-us-inner-image slide-right ${
                            image2Visible ? "visible" : ""
                        }`}
                    >
                        <img src={`${IMAGE_URL}/${about?.image_2}`} alt={about?.image_2} />
                    </div>
                </div>
            </div>
            <Footer />
        </div>
    );
}

export default AboutUs;
